import React, { useState, useEffect, useRef } from 'react';
import { db, collection, doc, setDoc, getDocs, query, where } from '../../firebase';
import { Layout, X, AlertCircle, Plus, Loader, Archive } from 'lucide-react';
import ImageUploader from './ImageUploader';
import styles from '../CSS/CreateSpaceModal.module.css';

const CreateSpaceModal = ({ onClose, spaces, type = 'spaces' }) => {
  const [name, setName] = useState('');
  const [location, setLocation] = useState(''); // 🆕 Estado para la ubicación
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // Auto-focus the input when modal opens
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);

    // Add escape key listener
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    // Click outside to close
    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    // Using mousedown instead of click for better mobile experience
    document.addEventListener('mousedown', handleOutsideClick);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [onClose]);

  // ✅ FUNCIÓN: Verificar si ya existe un espacio con ese nombre
  const checkIfSpaceExists = (spaceName) => {
    if (!spaces || !Array.isArray(spaces)) return false;
    
    return spaces.some(space => 
      space.name.toLowerCase().trim() === spaceName.toLowerCase().trim()
    );
  };

  // ✅ FUNCIÓN: Validación adicional en la base de datos
  const checkSpaceExistsInDB = async (spaceName) => {
    try {
      const spacesRef = collection(db, 'spaces');
      const q = query(spacesRef, where('name', '==', spaceName.trim()));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error verificando espacio en DB:', error);
      return false;
    }
  };

  // 🆕 FUNCIÓN: Manejar cambio de imagen
  const handleImageChange = (url) => {
    setImageUrl(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedName = name.trim();
    const trimmedLocation = location.trim(); // 🆕 Trim de la ubicación
    
    if (trimmedName === '') {
      setError('El nombre del espacio es requerido');
      inputRef.current.focus();
      return;
    }

    // ✅ VALIDACIÓN: Verificar duplicados localmente primero
    if (checkIfSpaceExists(trimmedName)) {
      setError('Ya existe un espacio con ese nombre. Por favor, elige un nombre diferente.');
      inputRef.current.focus();
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // ✅ VALIDACIÓN ADICIONAL: Verificar en la base de datos
      const existsInDB = await checkSpaceExistsInDB(trimmedName);
      if (existsInDB) {
        setError('Ya existe un espacio con ese nombre. Por favor, elige un nombre diferente.');
        inputRef.current.focus();
        setLoading(false);
        return;
      }

      const spacesRef = collection(db, 'spaces');
      const newSpace = {
        name: trimmedName,
        location: trimmedLocation || null, // 🆕 Guardar ubicación
        imageUrl: imageUrl || null,
        type: type, // 🆕 Guardar el tipo (spaces o cabinets)
        createdAt: new Date().toISOString()
      };
      const newDocRef = doc(spacesRef);
      
      await setDoc(newDocRef, newSpace);
      console.log('✅ Espacio creado con ubicación:', newSpace);
      onClose();
    } catch (err) {
      console.error('Error al crear el espacio:', err);
      setError('Ha ocurrido un error al crear el espacio. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div
        className={styles.modalContent}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className={styles.modalHeader}>
          <div className={styles.titleContainer}>
            {type === 'spaces' ? (
              <Layout className={styles.titleIcon} />
            ) : (
              <Archive className={styles.titleIcon} />
            )}
            <h2 id="modal-title" className={styles.title}>
              {type === 'spaces' ? 'Crear Nuevo Espacio' : 'Crear Nuevo Armario'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Cerrar modal"
          >
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <label htmlFor="space-name" className={styles.label}>
              {type === 'spaces' ? 'Nombre del espacio' : 'Nombre del armario'}
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="space-name"
                type="text"
                placeholder={type === 'spaces' ? 'Ej: Sala de Conferencias' : 'Ej: Armario 1'}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  // Limpiar error cuando el usuario empiece a escribir
                  if (error) setError('');
                }}
                className={`${styles.input} ${error ? styles.inputError : ''}`}
                ref={inputRef}
                maxLength={50}
                disabled={loading}
              />
              <span className={`${styles.characterCount} ${name.length > 40 ? styles.almostFull : ''}`}>
                {name.length}/50
              </span>
            </div>
            {error && (
              <div className={styles.error}>
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* 🆕 CAMPO DE UBICACIÓN */}
          <div className={styles.inputGroup}>
            <label htmlFor="space-location" className={styles.label}>
              Ubicación <span className={styles.optionalLabel}>(opcional)</span>
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="space-location"
                type="text"
                placeholder="Ej: Piso 2, Edificio A, Sala 301"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className={styles.input}
                maxLength={30}
                disabled={loading}
              />
              <span className={`${styles.characterCount} ${location.length > 25 ? styles.almostFull : ''}`}>
                {location.length}/30
              </span>
            </div>
          </div>

          {/* COMPONENTE DE SUBIDA DE IMAGEN */}
          <ImageUploader
            currentImage={imageUrl}
            onImageChange={handleImageChange}
            disabled={loading}
          />
        </form>

        <div className={styles.buttonGroup}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelButton}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className={styles.spinnerIcon} />
                Creando...
              </>
            ) : (
              <>
                <Plus />
                {type === 'spaces' ? 'Crear Espacio' : 'Crear Armario'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSpaceModal;